import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Banner } from '@wallyhealth/web-core'
import JoinStartPage from './JoinStartPage'
import JoinStudioPage from './JoinStudioPageV2'
import JoinPlanPage from './JoinPlanPage'
import { PlanType } from './shared'
import { useBilling } from './hooks'
import PaymentPage from 'components/PaymentPage'
import heapService from 'services/heapService'
import { useGeos } from 'hooks/useGeos'

const StepId = {
  START: 1,
  STUDIO: 2,
  PLAN: 3,
  PAYMENT: 4
}

const COPY_CONFIG = {
  regular: {
    banner: null,
    cta: 'Get Started'
  },
  disc50: {
    banner: 'Limited-time sale! Get Wally for $199 (usually $249).',
    cta: 'Get Offer'
  },
  disc75: {
    banner: 'Limited-time sale! Get Wally for $174 (usually $249).',
    cta: 'Get Offer'
  },
  disc100: {
    banner: 'ENDS AT MIDNIGHT: Get Wally for $149 (normally $249).',
    cta: 'Get Offer'
  }
}

const resolveCopy = (selector) => {
  return COPY_CONFIG[selector] || COPY_CONFIG.regular
}

const orderGeos = ({ geos, preferredGeoId }) => {
  if (!preferredGeoId) return geos

  const preferredGeo = geos.find(geo => geo.id === preferredGeoId)
  if (!preferredGeo) return geos

  return [
    preferredGeo,
    ...geos.filter(geo => geo.id !== preferredGeoId)
  ]
}

const JoinFlow = () => {
  const { geos, locations } = useGeos()
  const [search] = useSearchParams()

  const [stepId, setStepId] = useState(StepId.START)
  const [customer, setCustomer] = useState(null)
  const [planType, setPlanType] = useState(null)
  const [totalCostInCents, setTotalCostInCents] = useState(0)
  const [locationId, setLocationId] = useState(null)
  const [couponCode, setCouponCode] = useState(search.get('code'))

  const copySelector = search.get('cpy')
  const preferredGeoId = Number(search.get('g')) || null

  const { pricing, reloadPricing, isPricingLoading, prepSubscription } = useBilling({ couponCode })
  const orderedGeos = useMemo(() => {
    return orderGeos({ geos, preferredGeoId })
  }, [geos, preferredGeoId])

  const scrollToTop = () => (
    window.scrollTo({ top: 0, behavior: 'smooth' })
  )
  useEffect(() => {
    scrollToTop()
  }, [stepId])

  const applyCouponCode = ({ code }) => {
    setCouponCode(code)
    if (code === couponCode) {
      reloadPricing()
    }
  }

  const handleCustomerInfo = (info) => {
    setCustomer(info)
    setStepId(StepId.STUDIO)
  }

  const handleStudioBack = () => {
    setStepId(StepId.START)
  }

  const onLocationPicked = (locationId) => {
    setLocationId(locationId)
    setStepId(StepId.PLAN)
  }

  const handlePlanBack = () => {
    setStepId(StepId.STUDIO)
  }

  const onPlanSelected = async ({ planType, totalCostInCents }) => {
    setPlanType(planType)
    setTotalCostInCents(totalCostInCents)
    setStepId(StepId.PAYMENT)
  }

  const handlePaymentBack = () => {
    setStepId(StepId.PLAN)
  }

  const storeHeapUserProperties = () => {
    heapService.storeUserProperties({
      phone: customer.phone,
      email: customer.email,
      costInCents: totalCostInCents,
      location: locations.find(l => l.id === locationId)?.name,
      planType,
      couponCode
    })
  }

  const resolveCouponCode = () => {
    return (planType === PlanType.ANNUAL)
      ? couponCode
      : null
  }

  const handleSubscriptionPrep = () => {
    storeHeapUserProperties()

    return prepSubscription({
      billing: {
        planType,
        couponCode: resolveCouponCode()
      },
      patient: {
        email: customer.email,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone
      },
      location: locations.find(l => l.id === locationId)
    })
  }

  const copy = resolveCopy(copySelector)
  const hasBanner = !!copy.banner

  return (
    <div>

      {hasBanner &&
        <Banner
          text={copy.banner}
        />
      }

      {stepId === StepId.START &&
        <JoinStartPage
          onInfoCaptured={handleCustomerInfo}
          couponCode={couponCode}
          copy={resolveCopy(copySelector)}
          customer={customer}
        />
      }

      {stepId === StepId.STUDIO &&
        <JoinStudioPage
          onLocationPicked={onLocationPicked}
          onBack={handleStudioBack}
          geos={orderedGeos}
        />
      }

      {stepId === StepId.PLAN &&
        <JoinPlanPage
          onPlanSelected={onPlanSelected}
          onBack={handlePlanBack}
          pricing={pricing}
          isPricingLoading={isPricingLoading}
          reloadPricing={reloadPricing}
          applyCouponCode={applyCouponCode}
        />
      }

      {stepId === StepId.PAYMENT &&
        <PaymentPage
          customer={customer}
          totalCostInCents={totalCostInCents}
          planType={planType}
          prepSubscription={handleSubscriptionPrep}
          onBack={handlePaymentBack}
          viewName="JoinPaymentPage"
          description="Once you join your care team will text you how to book your first appointment. And if you change your mind before your first visit, we’ll refund you 100%."
        />
      }
    </div>
  )
}

export default JoinFlow
